import { useCallback } from 'react';
import { useAuth as useKeycloakAuth } from './keycloak/hooks';
import {
  UnifiedAuth,
  UnifiedAuthAuthenticated,
  UnifiedAuthHook,
  UnifiedAuthUnauthenticated,
  User,
} from './types';

function useLogin() {
  const keycloakAuth = useKeycloakAuth();

  const unifiedLogin = useCallback(
    async (username: string, password: string) => {
      if (!keycloakAuth.initialized || keycloakAuth.authenticated) {
        return;
      }

      return keycloakAuth.login(username, password);
    },
    [keycloakAuth],
  );

  return unifiedLogin;
}

function useAuth(authDone: true): UnifiedAuthAuthenticated;
function useAuth(authDone: false): UnifiedAuthUnauthenticated;
function useAuth(): UnifiedAuthHook;
function useAuth(authDone?: boolean) {
  const keycloakAuth = useKeycloakAuth();

  if (!keycloakAuth.initialized) {
    if (authDone === false || authDone === true) {
      throw new Error('Auth not initialized');
    }

    return { initialized: false };
  }

  if (keycloakAuth.authenticated) {
    if (authDone === false) {
      throw new Error('User should not be authenticated here');
    }

    return unifiedAuthToAuthenticatedHook(keycloakAuth);
  }

  if (authDone === true) {
    throw Error('User should be authenticated here');
  }

  const result: UnifiedAuthUnauthenticated = {
    initialized: true,
    authenticated: false,
  };

  return result;
}

function useUser() {
  const auth = useAuth(true);

  return auth.user;
}

const unifiedAuthToAuthenticatedHook = (
  auth: UnifiedAuth,
): UnifiedAuthAuthenticated => ({
  initialized: true,
  authenticated: true,
  authorized: auth.authorized,
  impersonator: auth.impersonator,
  logout: auth.logout,
  token: auth.token as () => string,
  user: auth.user as User,
});

export { useAuth, useUser, useLogin, useKeycloakAuth };
